import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import Lottie from 'lottie-web';
gsap.registerPlugin(ScrollTrigger);

const {registerModule} = window;

class Animation {
  constructor(element) {
    this.element = element;
    this.initialize();
    this.checkMediaQuery();
  }

  initialize() {
    this.bindUi();
    this.setVars();
  }

  checkMediaQuery() {
    this.matchMedia = gsap.matchMedia();

    this.matchMedia.add({
      isMobile: '(max-width: 600px)',
      isTablet: '(max-width: 1023px) and (min-width: 601px)',
      isDesktop: '(min-width: 1024px)',
      isPortrait: '(orientation: portrait)',
      isLandscape: '(orientation: landscape)',
    }, (context) => {
      let displayElement = true;
      if (this.displayConditions.displayOn && !this.displayConditions.mobile && context.conditions.isMobile) {
        displayElement = false;
      }
      if (this.displayConditions.displayOn && !this.displayConditions.tablet && context.conditions.isTablet) {
        displayElement = false;
      }
      if (this.displayConditions.displayOn && !this.displayConditions.desktop && context.conditions.isDesktop) {
        displayElement = false;
      }
      if (this.displayConditions.displayOn && !this.displayConditions.portrait && context.conditions.isPortrait) {
        displayElement = false;
      }
      if (this.displayConditions.displayOn && !this.displayConditions.landscape && context.conditions.isLandscape) {
        displayElement = false;
      }
      if (displayElement) {
        this.setupAnimation({
          target: this.animationWindow,
          path: this.desktopAnimationFile,
          animationDuration: this.animationDuration,
          scrub: this.animationScrub, // seconds it takes for the playhead to "catch up"
        });
      }
    });
  }

  setupAnimation(vars) {
    const playhead = {frame: 0};
    const target = gsap.utils.toArray(vars.target)[0];
    const scrollHint = document.querySelector('.scrollHint');
    const st = {trigger: target, pin: true, start: 'top top', end: `+=${vars.animationDuration}`, scrub: vars.scrub, onUpdate: (self) => {
      if (scrollHint && !scrollHint.classList.contains('scrollHint__hidden') && self.progress * 100 > this.showScrollHintFor) {
        scrollHint.classList.add('scrollHint__hidden');
      }
    }, onRefresh: () => {
      if (!this.jumpTarget || !this.unresolvedJump || this.jumpTarget.getBoundingClientRect().top <= 0) {
        this.unresolvedJump = false;
      } else {
        this.jumpTarget.scrollIntoView({behavior: 'instant'});
      }
    }};
    const ctx = gsap.context && gsap.context();
    const animation = Lottie.loadAnimation({
      container: target,
      renderer: vars.renderer || 'svg',
      loop: false,
      autoplay: false,
      path: vars.path,
      rendererSettings: vars.rendererSettings || {preserveAspectRatio: 'xMidYMid slice'},
    });
    animation.addEventListener('DOMLoaded', function() {
      const createTween = function() {
        animation.frameTween = gsap.to(playhead, {
          frame: animation.totalFrames - 1,
          ease: 'none',
          onUpdate: () => animation.goToAndStop(playhead.frame, true),
          scrollTrigger: st,
        });
        return () => animation.destroy && animation.destroy();
      };
      ctx && ctx.add ? ctx.add(createTween) : createTween();
      // in case there are any other ScrollTriggers on the page and the loading of this Lottie asset caused layout changes
      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    });
    return animation;
  }

  setVars() {
    const defaultAnimationDuration = 20000;
    const defaultAnimationScrub = 2;
    this.desktopAnimationFile = this.element.getAttribute('data-desktop-animation-file');
    this.animationDuration = parseFloat(this.element.getAttribute('data-animation-duration')) || defaultAnimationDuration;
    this.animationScrub = parseFloat(this.element.getAttribute('data-animation-scrub')) || defaultAnimationScrub;
    this.showScrollHintFor = parseFloat(this.element.getAttribute('data-show-scroll-hint-for'));
    this.element.style.minHeight = `${parseFloat(this.element.getAttribute('data-animation-duration')) || defaultAnimationDuration}px`;
    this.displayConditions = {
      displayOn: this.element.classList.contains('display-on'),
      mobile: this.element.classList.contains('display-on__Mobile'),
      tablet: this.element.classList.contains('display-on__Tablet'),
      desktop: this.element.classList.contains('display-on__Desktop'),
      portrait: this.element.classList.contains('display-on__Portrait'),
      landscape: this.element.classList.contains('display-on__Landscape'),
    };
    if (window.location.hash && document.getElementById(window.location.hash.slice(1)) && !['reload', 'back_forward'].includes(window.performance?.getEntries()[0]?.type)) {
      this.unresolvedJump = true;
      this.jumpTarget = document.getElementById(window.location.hash.slice(1));
    }
  }

  bindUi() {
    this.animationWindow = this.element.querySelector('.animationWindow');
  }
}

registerModule('Animation', Animation);
